import React, { useState } from 'react';
import Swal from 'sweetalert2';
import api from '../services/api';
import { getSwalTheme } from '../utils/swalTheme';
import { DRIVE_TYPES } from '../constants/vehicles';
import useModalAccessibility from '../hooks/useModalAccessibility';
import './EditShiftModal.css';

const EditDriverPostModal = ({ post, onClose, onUpdate, isAdmin = false }) => {
  const modalRef = useModalAccessibility(onClose);
  const apiPath = isAdmin ? '/admin/driver-posts' : '/driver-posts';

  // Convert vehicle_types CSV to single value for dropdown
  const parseVehicleType = () => {
    if (!post.vehicle_types) return '';
    const arr = post.vehicle_types.split(',').map(v => v.trim()).filter(Boolean);
    if (arr.length === 0) return '';
    return arr[0];
  };

  const [formData, setFormData] = useState({
    title: post.title,
    description: post.description || '',
    vehicle_type: parseVehicleType(),
    drive_type: post.drive_type || '',
    wav: !!post.wav,
    shift_type: post.shift_type || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = {
        title: formData.title,
        description: formData.description,
        vehicle_types: formData.vehicle_type ? [formData.vehicle_type] : [],
        drive_type: formData.drive_type || null,
        wav: formData.wav,
        shift_type: formData.shift_type,
      };

      await api.put(`${apiPath}/${post.id}`, submitData);
      onUpdate();
      onClose();
      Swal.fire({
        ...getSwalTheme(),
        icon: 'success',
        title: 'Driver Listing Updated!',
        text: 'Your listing has been updated.'
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update driver listing');
    } finally {
      setLoading(false);
    }
  };

  const shiftTypeOptions = [
    { value: 'dov', label: 'DOV (Drive Own Vehicle)' },
    { value: 'weekly_24', label: 'Weekly 24hr' },
    { value: 'weekly_day', label: 'Weekly Day' },
    { value: 'weekly_night', label: 'Weekly Night' },
    { value: 'daily_day', label: 'Daily Day' },
    { value: 'daily_night', label: 'Daily Night' },
    { value: 'daily_24', label: 'Daily 24hr' }
  ];

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        ref={modalRef}
        className="edit-modal-content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-post-title"
      >
        <button
          type="button"
          className="modal-close"
          onClick={onClose}
          aria-label="Close dialog"
        >
          &times;
        </button>

        <div className="modal-header">
          <h2 id="edit-post-title">Edit Driver Listing</h2>
        </div>

        <form onSubmit={handleSubmit} className="edit-shift-form">
          {error && <div className="form-error">{error}</div>}

          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="vehicle_type">Work Type</label>
              <select
                id="vehicle_type"
                name="vehicle_type"
                value={formData.vehicle_type}
                onChange={handleChange}
              >
                <option value="">Any / Flexible</option>
                <option value="yellow_cab">Yellow Cab</option>
                <option value="green_boro">Green Boro</option>
                <option value="black_car">Black Car</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="drive_type">Drive Type</label>
              <select
                id="drive_type"
                name="drive_type"
                value={formData.drive_type}
                onChange={handleChange}
              >
                <option value="">Any / Flexible</option>
                {DRIVE_TYPES.map(dt => (
                  <option key={dt.value} value={dt.value}>{dt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <div style={{ display: 'flex' }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="wav"
                  checked={formData.wav}
                  onChange={handleCheckboxChange}
                />
                WAV
              </label>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="shift_type">Shift Type *</label>
            <select
              id="shift_type"
              name="shift_type"
              value={formData.shift_type}
              onChange={handleChange}
              required
            >
              <option value="">Select shift type</option>
              {shiftTypeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDriverPostModal;
